import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Copy, FileDown, Receipt, AlertTriangle, Printer, Check, Mail, Bell, Sparkles, FileText, ChevronDown, Building2, Pencil, Save, X, Send, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { SendEmailDialog } from '../../components/SendEmailDialog';
import { FollowUpDialog } from '../../components/AIPanels';
import { useAuth } from '../../lib/auth';
import { OverdueBadge, daysOverdue } from '../../components/OverdueBadge';
import { saveDraft, loadDraft, clearDraft, formatSavedAt } from '../../lib/drafts';

const STATO_COLORS = {
    bozza: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
    inviata: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
    pagata: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]',
    scaduta: 'bg-[hsl(0_75%_45%)] text-white font-bold',
};

const TrafficLight = ({ scadenza }) => {
    const { t } = useTranslation();
    const today = new Date();
    const scad = new Date(scadenza);
    const days = Math.ceil((scad - today) / (1000 * 60 * 60 * 24));
    let color = 'bg-[hsl(145_55%_34%)]';
    let label = t('pages.fat_due_days', { n: days });
    if (days < 0) { color = 'bg-[hsl(0_72%_52%)]'; label = t('pages.fat_due_overdue_label'); }
    else if (days < 7) { color = 'bg-[hsl(45_100%_52%)]'; label = t('pages.fat_due_days', { n: days }); }
    return <Badge className={`${color} text-white hover:${color}`} data-testid="scadenzario-badge">{label}</Badge>;
};

const emptyLine = () => ({ descrizione: '', quantita: 1, unita: 'n', prezzo_unitario: 0, aliquota_iva: 22 });

export default function Fatture() {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [cantieri, setCantieri] = useState([]);
    const [preventivi, setPreventivi] = useState([]);
    const [proforme, setProforme] = useState([]);
    const [kpi, setKpi] = useState({ fatturato_anno: 0, da_incassare: 0, scadute: 0 });
    const [filter, setFilter] = useState('all');
    const [tab, setTab] = useState('lista');
    const [openNew, setOpenNew] = useState(false);
    const [openDetail, setOpenDetail] = useState(null);
    const [emailDialog, setEmailDialog] = useState(null);
    const [followUp, setFollowUp] = useState(null);
    const [openFromQuote, setOpenFromQuote] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    // Edit mode
    const [editMode, setEditMode] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    // SDI
    const [sdiSending, setSdiSending] = useState(false);
    const [sdiNotConfiguredOpen, setSdiNotConfiguredOpen] = useState(false);
    const [form, setForm] = useState({
        cliente_nome: '', cliente_piva: '', cliente_codice_fiscale: '', cliente_sdi: '0000000', cliente_pec: '', cliente_email: '',
        cliente_indirizzo: '', cliente_cap: '', cliente_comune: '', cliente_provincia: '',
        cantiere_id: '', data_emissione: '', data_scadenza: '',
        payment_terms: (user?.default_payment_terms) || '30gg_fine_mese',
        ritenuta_acconto: false, ritenuta_percentuale: 20,
        causale: '', note: '', metodo_pagamento: 'MP05',
        righe: [emptyLine()],
        preventivo_riferimento: '',
    });

    // When user is loaded, sync default payment terms (in case empty)
    useEffect(() => {
        if (user?.default_payment_terms && !form.payment_terms) {
            setForm((f) => ({ ...f, payment_terms: user.default_payment_terms }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.default_payment_terms]);

    // --- Draft auto-save (fattura) ---
    const FAT_DRAFT_SLOT = 'fattura';
    const [draftHint, setDraftHint] = useState(null);
    const formInitialized = React.useRef(false);

    // Returns the meaningful fields for "draft-worthiness" detection
    const draftSignals = (f) => ({
        cliente_nome: f.cliente_nome || '',
        causale: f.causale || '',
        first_line_desc: (f.righe && f.righe[0] && f.righe[0].descrizione) || '',
    });

    useEffect(() => {
        if (!openNew) return;
        if (!formInitialized.current) { formInitialized.current = true; return; }
        const id = window.setTimeout(() => {
            // Custom isMeaningful using signals
            const signals = draftSignals(form);
            const hasMeaning = Object.values(signals).some((v) => v && String(v).trim() !== '');
            if (hasMeaning) saveDraft(FAT_DRAFT_SLOT, form);
            else clearDraft(FAT_DRAFT_SLOT);
        }, 500);
        return () => window.clearTimeout(id);
    }, [form, openNew]);

    useEffect(() => {
        if (openNew) {
            const d = loadDraft(FAT_DRAFT_SLOT);
            if (d && d.data) setDraftHint({ savedAt: d.savedAt });
            else setDraftHint(null);
            formInitialized.current = false;
        } else {
            setDraftHint(null);
        }
    }, [openNew]);

    const resumeFatDraft = () => {
        const d = loadDraft(FAT_DRAFT_SLOT);
        if (!d || !d.data) { setDraftHint(null); return; }
        // Defensive merge: ensure required nested shapes exist
        const merged = { ...form, ...d.data };
        if (!Array.isArray(merged.righe) || merged.righe.length === 0) {
            merged.righe = [emptyLine()];
        }
        setForm(merged);
        setDraftHint(null);
    };

    const discardFatDraft = () => {
        clearDraft(FAT_DRAFT_SLOT);
        setDraftHint(null);
        toast.success(t('pages.draft_discarded'));
    };

    const handleOpenNewChange = (isOpen) => {
        if (!isOpen) {
            const signals = draftSignals(form);
            const hasMeaning = Object.values(signals).some((v) => v && String(v).trim() !== '');
            if (hasMeaning) {
                saveDraft(FAT_DRAFT_SLOT, form);
                toast.info(t('pages.draft_close_save_hint'), { duration: 3000 });
            }
        }
        setOpenNew(isOpen);
    };

    // Payment terms options (shared with Preventivi/Proforme)
    const PAYMENT_TERM_OPTIONS = [
        { value: 'vista_fattura', labelKey: 'pages.prev_pt_vista_fattura' },
        { value: 'fine_mese', labelKey: 'pages.prev_pt_fine_mese' },
        { value: '30gg_fine_mese', labelKey: 'pages.prev_pt_30gg_fine_mese' },
        { value: '60gg_fine_mese', labelKey: 'pages.prev_pt_60gg_fine_mese' },
    ];
    // Compute due date locally so the user sees it before saving.
    const computeScadenza = (dataEmis, paymentTerms) => {
        if (!paymentTerms) return '';
        const today = new Date();
        let base = today;
        if (dataEmis) {
            const d = new Date(dataEmis);
            if (!Number.isNaN(d.getTime())) base = d;
        }
        const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        if (paymentTerms === 'vista_fattura') return isoDate(base);
        const monthsAhead = paymentTerms === 'fine_mese' ? 0 : paymentTerms === '30gg_fine_mese' ? 1 : paymentTerms === '60gg_fine_mese' ? 2 : 0;
        const ny = base.getFullYear() + Math.floor((base.getMonth() + monthsAhead) / 12);
        const nm = ((base.getMonth() + monthsAhead) % 12 + 12) % 12; // 0-11
        const lastDay = new Date(ny, nm + 1, 0).getDate();
        return isoDate(new Date(ny, nm, lastDay));
    };

    const STATUS_LABEL = {
        bozza: t('pages.fat_status_bozza'),
        inviata: t('pages.fat_status_inviata'),
        pagata: t('pages.fat_status_pagata'),
        scaduta: t('pages.fat_status_scaduta'),
    };
    const FILTER_LABEL = {
        all: t('pages.fat_filter_all'),
        bozza: t('pages.fat_status_bozza'),
        inviata: t('pages.fat_status_inviata'),
        pagata: t('pages.fat_status_pagata'),
        scaduta: t('pages.fat_status_scaduta'),
    };

    const load = async () => {
        const [f, c, k, p, pf] = await Promise.all([
            api.get('/fatture/list'),
            api.get('/cantieri/list'),
            api.get('/fatture/kpi'),
            api.get('/preventivi/list').catch(() => ({ data: [] })),
            api.get('/proforme/list').catch(() => ({ data: [] })),
        ]);
        setItems(f.data);
        setCantieri(c.data);
        setKpi(k.data);
        setPreventivi(p.data || []);
        setProforme(pf.data || []);
    };
    useEffect(() => { load(); }, []);

    // Auto-open invoice from ?open=ID querystring (used when redirected from Preventivi → Genera Fattura)
    useEffect(() => {
        const openId = searchParams.get('open');
        if (openId && items.length > 0) {
            const f = items.find((x) => x.id === openId);
            if (f) {
                setOpenDetail(f);
                // remove the param after consuming
                searchParams.delete('open');
                setSearchParams(searchParams, { replace: true });
            }
        }
    }, [items, searchParams, setSearchParams]);

    // Reset edit mode when closing detail
    useEffect(() => {
        if (!openDetail) {
            setEditMode(false);
            setEditForm(null);
        }
    }, [openDetail]);

    // ---- Edit mode helpers (detail dialog) ----
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const startEdit = () => {
        if (!openDetail) return;
        setEditForm({
            cliente_nome: openDetail.cliente_nome || '',
            cliente_piva: openDetail.cliente_piva || '',
            cliente_codice_fiscale: openDetail.cliente_codice_fiscale || '',
            cliente_sdi: openDetail.cliente_sdi || '0000000',
            cliente_pec: openDetail.cliente_pec || '',
            cliente_email: openDetail.cliente_email || '',
            cliente_indirizzo: openDetail.cliente_indirizzo || '',
            data_emissione: openDetail.data_emissione || '',
            data_scadenza: openDetail.data_scadenza || '',
            causale: openDetail.causale || '',
            note: openDetail.note || '',
            metodo_pagamento: openDetail.metodo_pagamento || 'MP05',
            ritenuta_acconto: !!openDetail.ritenuta_acconto,
            ritenuta_percentuale: openDetail.ritenuta_percentuale || 20,
            righe: JSON.parse(JSON.stringify(openDetail.righe || [])),
        });
        setEditMode(true);
    };
    const cancelEdit = () => { setEditMode(false); setEditForm(null); };
    const updEditLine = (i, k, v) => {
        setEditForm((f) => {
            const r = [...f.righe];
            r[i] = { ...r[i], [k]: ['quantita', 'prezzo_unitario', 'aliquota_iva'].includes(k) ? (parseFloat(v) || 0) : v };
            return { ...f, righe: r };
        });
    };
    const addEditLine = () => setEditForm((f) => ({ ...f, righe: [...f.righe, emptyLine()] }));
    const delEditLine = (i) => setEditForm((f) => ({ ...f, righe: f.righe.filter((_, j) => j !== i) }));
    const editTotali = () => {
        if (!editForm) return { imp: 0, iva: 0, rit: 0, tot: 0 };
        const imp = editForm.righe.reduce((s, r) => s + (r.quantita * r.prezzo_unitario), 0);
        const iva = editForm.righe.reduce((s, r) => s + (r.quantita * r.prezzo_unitario * r.aliquota_iva / 100), 0);
        const rit = editForm.ritenuta_acconto ? (imp * editForm.ritenuta_percentuale / 100) : 0;
        return { imp: round2(imp), iva: round2(iva), rit: round2(rit), tot: round2(imp + iva - rit) };
    };
    const saveEdit = async () => {
        if (!openDetail || !editForm) return;
        setSavingEdit(true);
        try {
            const payload = { ...editForm };
            const res = await api.put(`/fatture/${openDetail.id}`, payload);
            const updated = res.data;
            setOpenDetail(updated);
            setItems((prev) => prev.map((p) => p.id === updated.id ? updated : p));
            toast.success(t('pages.fat_edit_saved'));
            setEditMode(false);
            setEditForm(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('pages.fat_edit_save_err'));
        } finally {
            setSavingEdit(false);
        }
    };

    // ---- SDI ----
    const sendToSDI = async () => {
        if (!openDetail) return;
        setSdiSending(true);
        try {
            const res = await api.post(`/fatture/${openDetail.id}/sdi/invio`);
            const sdi = res.data || {};
            // Backend returns 200 with status="error" when the provider rejected the submission.
            // We must check the status field, NOT just the HTTP code.
            if (sdi.status === 'error') {
                toast.error(t('pages.fat_sdi_error'), {
                    description: sdi.error || undefined,
                });
            } else {
                // 'sent' or 'queued' — show success and expose the SDI protocol id (if any) so
                // the user can track the submission with the provider.
                toast.success(t('pages.fat_sdi_sent'), {
                    description: sdi.sdi_id
                        ? `${sdi.provider ? sdi.provider.toUpperCase() + ' · ' : ''}${sdi.sdi_id}`
                        : undefined,
                });
            }
            // Refresh
            const refreshed = await api.get('/fatture/list');
            setItems(refreshed.data);
            const updated = refreshed.data.find((x) => x.id === openDetail.id);
            if (updated) setOpenDetail(updated);
        } catch (e) {
            const detail = e.response?.data?.detail;
            const code = (typeof detail === 'object' && detail) ? detail.code : null;
            if (e.response?.status === 501 || code === 'SDI_NOT_CONFIGURED') {
                setSdiNotConfiguredOpen(true);
            } else {
                toast.error((typeof detail === 'string' ? detail : detail?.message) || t('pages.fat_sdi_error'));
            }
        } finally {
            setSdiSending(false);
        }
    };


    const addLine = () => setForm({ ...form, righe: [...form.righe, emptyLine()] });
    const updLine = (i, k, v) => {
        const r = [...form.righe];
        r[i] = { ...r[i], [k]: ['quantita', 'prezzo_unitario', 'aliquota_iva'].includes(k) ? parseFloat(v) || 0 : v };
        setForm({ ...form, righe: r });
    };
    const delLine = (i) => setForm({ ...form, righe: form.righe.filter((_, j) => j !== i) });

    const totali = () => {
        const imp = form.righe.reduce((s, r) => s + r.quantita * r.prezzo_unitario, 0);
        const iva = form.righe.reduce((s, r) => s + r.quantita * r.prezzo_unitario * r.aliquota_iva / 100, 0);
        const rit = form.ritenuta_acconto ? imp * form.ritenuta_percentuale / 100 : 0;
        return { imp, iva, rit, tot: imp + iva - rit };
    };

    const create = async () => {
        if (!form.cliente_nome) { toast.error(t('pages.fat_toast_no_client')); return; }
        if (form.righe.length === 0 || form.righe.every(r => !r.descrizione)) { toast.error(t('pages.fat_toast_no_line')); return; }
        try {
            await api.post('/fatture/create', form);
            toast.success(t('pages.fat_toast_created'));
            clearDraft(FAT_DRAFT_SLOT);
            setOpenNew(false);
            setForm({ cliente_nome: '', cliente_piva: '', cliente_codice_fiscale: '', cliente_sdi: '0000000', cliente_pec: '', cliente_email: '', cliente_indirizzo: '', cliente_cap: '', cliente_comune: '', cliente_provincia: '', cantiere_id: '', data_emissione: '', data_scadenza: '', ritenuta_acconto: false, ritenuta_percentuale: 20, causale: '', note: '', metodo_pagamento: 'MP05', righe: [emptyLine()] });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const setStato = async (id, stato) => {
        await api.put(`/fatture/${id}`, { stato });
        toast.success(t('pages.fat_toast_marked', { stato: STATUS_LABEL[stato] || stato }));
        load();
    };

    const remove = async (id) => {
        if (!window.confirm(t('common.confirm_delete'))) return;
        await api.delete(`/fatture/${id}`);
        load();
    };

    const duplica = async (id) => {
        await api.post(`/fatture/${id}/duplica`);
        toast.success(t('pages.fat_toast_duplicated'));
        load();
    };

    const applyFromQuote = async (preventivoId) => {
        try {
            const res = await api.post(`/fatture/da-preventivo/${preventivoId}`);
            const d = res.data;
            setForm((f) => ({
                ...f,
                cliente_nome: d.cliente_nome || f.cliente_nome,
                cliente_indirizzo: d.cliente_indirizzo || f.cliente_indirizzo,
                righe: (d.righe && d.righe.length) ? d.righe : f.righe,
                causale: d.causale || f.causale,
                preventivo_riferimento: d.preventivo_riferimento || '',
            }));
            setOpenFromQuote(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const companyInitials = (user?.company_name || user?.full_name || 'C').split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');

    const downloadXML = async (id) => {
        const token = localStorage.getItem('cantiere_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fatture/${id}/xml`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { toast.error(t('pages.fat_toast_xml_err')); return; }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const cd = res.headers.get('content-disposition') || '';
        const m = cd.match(/filename="([^"]+)"/);
        a.download = m ? m[1] : 'fattura.xml';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('pages.fat_toast_xml_ok'));
    };

    const downloadPDF = async (id) => {
        const token = localStorage.getItem('cantiere_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fatture/${id}/pdf`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { toast.error(t('pages.fat_toast_pdf_err') || 'Errore download PDF'); return; }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const cd = res.headers.get('content-disposition') || '';
        const m = cd.match(/filename="([^"]+)"/);
        a.download = m ? m[1] : 'fattura.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('pages.fat_toast_pdf_ok') || 'PDF scaricato');
    };

    const filtered = items.filter((f) => filter === 'all' || f.stato === filter);
    const scadenzario = items
        .filter((f) => f.stato === 'inviata')
        .sort((a, b) => new Date(a.data_scadenza) - new Date(b.data_scadenza));
    const totDaIncassare = scadenzario.reduce((s, f) => s + (f.totale || 0), 0);

    return (
        <div data-testid="fatture-page" className="pb-20 md:pb-0">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl">{t('pages.fat_title')}</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.fat_subtitle')}</p>
                </div>
                <Button onClick={() => setOpenNew(true)} className="hidden sm:flex bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="fatture-new-button"><Plus className="w-4 h-4" /> {t('pages.fat_new')}</Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_kpi_year')}</div><div className="font-display text-3xl tabular-nums">€{kpi.fatturato_anno.toLocaleString('it-IT', { maximumFractionDigits: 0 })}</div></Card>
                <Card className="p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_kpi_receivable')}</div><div className="font-display text-3xl tabular-nums">€{kpi.da_incassare.toLocaleString('it-IT', { maximumFractionDigits: 0 })}</div></Card>
                <Card className="p-4 border-[hsl(0_72%_52%)]/30"><div className="text-xs text-[hsl(0_72%_52%)] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t('pages.fat_kpi_overdue')}</div><div className="font-display text-3xl tabular-nums text-[hsl(0_72%_52%)]">€{kpi.scadute.toLocaleString('it-IT', { maximumFractionDigits: 0 })}</div></Card>
                <Card className="p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_kpi_total')}</div><div className="font-display text-3xl tabular-nums">{items.length}</div></Card>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="lista" data-testid="tab-lista">{t('pages.fat_tab_list')}</TabsTrigger>
                    <TabsTrigger value="scadenzario" data-testid="tab-scadenzario">{t('pages.fat_tab_due')}</TabsTrigger>
                    <TabsTrigger value="proforme" data-testid="tab-proforme">{t('pages.fat_tab_proforme') || 'Proforme SAL'}</TabsTrigger>
                </TabsList>

                <TabsContent value="lista">
                    <div className="flex gap-2 mb-4">
                        {['all', 'bozza', 'inviata', 'pagata', 'scaduta'].map((s) => (
                            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === s ? 'bg-[hsl(220_13%_10%)] text-white border-[hsl(220_13%_10%)]' : 'bg-white border-[hsl(var(--border))]'}`} data-testid={`filter-${s}`}>
                                {FILTER_LABEL[s]}
                            </button>
                        ))}
                    </div>

                    <Card className="overflow-x-auto">
                        {filtered.length === 0 ? (
                            <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                                <Receipt className="w-12 h-12 mx-auto mb-3" />
                                <p>{t('pages.fat_empty_filter')}</p>
                            </div>
                        ) : (
                            <>
                            <table className="w-full text-sm hidden md:table">
                                <thead className="bg-[hsl(var(--muted))]">
                                    <tr className="text-left">
                                        <th className="p-3">{t('pages.fat_col_numero')}</th>
                                        <th className="p-3">{t('pages.fat_col_cliente')}</th>
                                        <th className="p-3">{t('pages.fat_col_emission')}</th>
                                        <th className="p-3">{t('pages.fat_col_due')}</th>
                                        <th className="p-3 text-right">{t('pages.fat_col_total')}</th>
                                        <th className="p-3">{t('pages.fat_col_status')}</th>
                                        <th className="p-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((f) => {
                                        const overdue = f.stato === 'scaduta' || (f.stato === 'inviata' && daysOverdue(f.data_scadenza) >= 1);
                                        return (
                                        <tr key={f.id} className={`border-t ${overdue ? 'bg-[hsl(0_80%_98%)]' : ''}`} data-testid={`fattura-row-${f.id}`}>
                                            <td className="p-3 font-mono text-xs">{f.numero}</td>
                                            <td className="p-3 font-semibold">{f.cliente_nome}</td>
                                            <td className="p-3 text-xs">{f.data_emissione}</td>
                                            <td className={`p-3 text-xs ${overdue ? 'text-[hsl(0_75%_45%)] font-bold' : ''}`}>{f.data_scadenza}</td>
                                            <td className="p-3 text-right font-display text-lg tabular-nums">€{f.totale?.toLocaleString('it-IT')}</td>
                                            <td className="p-3">
                                                {f.stato === 'scaduta'
                                                    ? <OverdueBadge dataScadenza={f.data_scadenza} size="sm" />
                                                    : <Badge className={STATO_COLORS[f.stato]}>{STATUS_LABEL[f.stato] || f.stato}</Badge>
                                                }
                                            </td>
                                            <td className="p-3 text-right whitespace-nowrap">
                                                <Button size="sm" variant="ghost" onClick={() => setOpenDetail(f)}>{t('common.view')}</Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEmailDialog({fattura: f, type: 'send'})} title={t('pages.fat_action_send_email')} data-testid={`fatture-email-${f.id}`}><Mail className="w-4 h-4" /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => downloadPDF(f.id)} title={t('pages.fat_action_pdf') || 'Scarica PDF'} data-testid={`fatture-pdf-${f.id}`} className="gap-1"><FileText className="w-4 h-4" /> <span className="text-xs">PDF</span></Button>
                                                <Button size="sm" variant="ghost" onClick={() => downloadXML(f.id)} title={t('pages.fat_action_xml')} data-testid={`fatture-xml-${f.id}`} className="gap-1"><FileDown className="w-4 h-4" /> <span className="text-xs">XML</span></Button>
                                                <Button size="sm" variant="ghost" onClick={() => duplica(f.id)} title={t('pages.fat_action_duplicate')}><Copy className="w-4 h-4" /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {/* Mobile cards */}
                            <ul className="md:hidden divide-y divide-[hsl(var(--border))]">
                                {filtered.map((f) => {
                                    const overdue = f.stato === 'scaduta' || (f.stato === 'inviata' && daysOverdue(f.data_scadenza) >= 1);
                                    return (
                                    <li key={f.id} className={`p-4 ${overdue ? 'bg-[hsl(0_80%_98%)]' : ''}`} data-testid={`fattura-card-${f.id}`}>
                                        <button onClick={() => setOpenDetail(f)} className="w-full text-left">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{f.numero}</span>
                                                <span className="font-display text-lg tabular-nums">€{f.totale?.toLocaleString('it-IT')}</span>
                                            </div>
                                            <div className="font-semibold truncate">{f.cliente_nome}</div>
                                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                                                {f.stato === 'scaduta'
                                                    ? <OverdueBadge dataScadenza={f.data_scadenza} size="md" />
                                                    : <Badge className={STATO_COLORS[f.stato] + ' text-xs'}>{STATUS_LABEL[f.stato] || f.stato}</Badge>
                                                }
                                                <span className={`text-xs ${overdue ? 'text-[hsl(0_75%_45%)] font-bold' : 'text-[hsl(var(--muted-foreground))]'}`}>{f.data_scadenza}</span>
                                            </div>
                                        </button>
                                        <div className="mt-2 flex justify-end gap-1">
                                            <Button size="sm" variant="ghost" onClick={() => setEmailDialog({fattura: f, type: 'send'})} className="h-9"><Mail className="w-4 h-4" /></Button>
                                            <Button size="sm" variant="ghost" onClick={() => downloadPDF(f.id)} className="h-9 gap-1" title={t('pages.fat_action_pdf') || 'Scarica PDF'}><FileText className="w-4 h-4" /> <span className="text-xs">PDF</span></Button>
                                            <Button size="sm" variant="ghost" onClick={() => downloadXML(f.id)} className="h-9 gap-1"><FileDown className="w-4 h-4" /> <span className="text-xs">XML</span></Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="h-9 text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                                        </div>
                                    </li>
                                    );
                                })}
                            </ul>
                            </>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="scadenzario">
                    <Card className="p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-2xl">{t('pages.fat_due_title')}</h3>
                            <div className="text-right"><div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_due_total')}</div><div className="font-display text-2xl tabular-nums">€{totDaIncassare.toLocaleString('it-IT')}</div></div>
                        </div>
                        {scadenzario.length === 0 ? (
                            <div className="text-center py-8 text-[hsl(var(--muted-foreground))]"><Check className="w-10 h-10 mx-auto mb-2 text-[hsl(145_55%_34%)]" /><p>{t('pages.fat_due_all_ok')}</p></div>
                        ) : (
                            <div className="space-y-2">
                                {scadenzario.map((f) => (
                                    <div key={f.id} className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))]">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{f.cliente_nome}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{f.numero} · {t('pages.fat_due_scade')} {f.data_scadenza}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <TrafficLight scadenza={f.data_scadenza} />
                                            <div className="font-display text-lg tabular-nums w-24 text-right">€{f.totale?.toLocaleString('it-IT')}</div>
                                            <Button size="sm" variant="outline" onClick={() => setEmailDialog({fattura: f, type: 'reminder'})} className="gap-1" title={t('pages.fat_action_send_reminder_title')}><Bell className="w-4 h-4" /> {t('pages.fat_action_send_reminder')}</Button>
                                            <Button size="sm" onClick={() => setStato(f.id, 'pagata')} className="bg-[hsl(145_55%_34%)] text-white hover:bg-[hsl(145_55%_28%)]">{t('pages.fat_action_paid')}</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="proforme">
                    {(() => {
                        // Group proforme by cantiere_id (only those linked to a cantiere/SAL)
                        const cantieriById = Object.fromEntries((cantieri || []).map((c) => [c.id, c]));
                        const groups = {};
                        (proforme || []).forEach((p) => {
                            const cid = p.cantiere_id || '_orphan';
                            if (!groups[cid]) groups[cid] = { cantiere: cantieriById[cid] || null, items: [] };
                            groups[cid].items.push(p);
                        });
                        const entries = Object.entries(groups).sort((a, b) => {
                            // Cantieri attivi first, then by name
                            const aa = a[1].cantiere?.avanzamento || 0;
                            const bb = b[1].cantiere?.avanzamento || 0;
                            return bb - aa;
                        });

                        const pfStateColors = {
                            bozza: 'bg-[hsl(var(--accent))]/15 text-[hsl(35_90%_35%)] border-[hsl(var(--accent))]',
                            emessa: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)] border-[hsl(210_90%_70%)]',
                            pagata: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)] border-[hsl(145_55%_60%)]',
                            scaduta: 'bg-[hsl(0_75%_45%)] text-white border-[hsl(0_75%_45%)] font-bold',
                            annullata: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]',
                        };
                        const pfStateLabel = {
                            bozza: t('pages.fat_pf_bozza') || 'BOZZA da inviare',
                            emessa: t('pages.fat_pf_emessa') || 'Inviata · in attesa',
                            pagata: t('pages.fat_pf_pagata') || 'Pagata',
                            scaduta: t('pages.prev_proforma_stato_scaduta') || 'Scaduta',
                            annullata: t('pages.fat_pf_annullata') || 'Annullata',
                        };

                        if (entries.length === 0) {
                            return (
                                <Card className="p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="proforme-empty">
                                    <FileText className="w-12 h-12 mx-auto mb-3" />
                                    <p className="mb-1 font-semibold">{t('pages.fat_pf_empty_title') || 'Nessuna proforma in corso'}</p>
                                    <p className="text-sm">{t('pages.fat_pf_empty_body') || 'Le proforme dei SAL appariranno qui automaticamente quando l\'avanzamento dei lavori raggiunge le soglie configurate.'}</p>
                                </Card>
                            );
                        }
                        return (
                            <div className="space-y-4">
                                {entries.map(([cid, g]) => {
                                    const cant = g.cantiere;
                                    const avanz = cant?.avanzamento || 0;
                                    const totalCantiere = cant?.importo || 0;
                                    const incassato = (g.items || []).filter((p) => p.stato === 'pagata').reduce((s, p) => s + (p.importo || 0), 0);
                                    const hasFatturaFinale = !!cant?.fattura_finale_id;
                                    return (
                                        <Card key={cid} className="p-5" data-testid={`proforme-cantiere-${cid}`}>
                                            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Building2 className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                                                        <h3 className="font-display text-xl truncate">{cant?.nome || t('pages.fat_pf_unknown_cantiere') || 'Cantiere non collegato'}</h3>
                                                        {hasFatturaFinale && (
                                                            <Badge className="bg-[hsl(220_13%_10%)] text-white text-xs">{t('pages.fat_pf_final_done') || 'Fattura finale emessa'}</Badge>
                                                        )}
                                                    </div>
                                                    {cant?.cliente && <div className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">{cant.cliente}</div>}
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-right">
                                                        <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.fat_pf_progress') || 'Avanzamento'}</div>
                                                        <div className="font-display text-lg tabular-nums">{Math.round(avanz)}%</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.fat_pf_collected') || 'Incassato'}</div>
                                                        <div className="font-display text-lg tabular-nums">€{incassato.toLocaleString('it-IT', { maximumFractionDigits: 0 })}<span className="text-xs text-[hsl(var(--muted-foreground))]"> / €{Number(totalCantiere).toLocaleString('it-IT', { maximumFractionDigits: 0 })}</span></div>
                                                    </div>
                                                    {cant?.id && (
                                                        <Button size="sm" variant="outline" onClick={() => navigate(`/app/sal?cantiere=${cant.id}`)} data-testid={`proforme-goto-sal-${cant.id}`} className="gap-1">
                                                            <FileText className="w-3.5 h-3.5" /> {t('pages.fat_pf_open_sal') || 'Apri SAL'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-[hsl(var(--muted))]/40">
                                                        <tr className="text-left">
                                                            <th className="p-2 font-medium text-xs">{t('pages.fat_pf_col_num') || 'Numero'}</th>
                                                            <th className="p-2 font-medium text-xs">{t('pages.fat_pf_col_tranche') || 'Tranche'}</th>
                                                            <th className="p-2 font-medium text-xs">{t('pages.fat_pf_col_state') || 'Stato'}</th>
                                                            <th className="p-2 font-medium text-xs">{t('pages.fat_pf_col_date') || 'Data'}</th>
                                                            <th className="p-2 font-medium text-xs text-right">{t('pages.fat_pf_col_amount') || 'Importo'}</th>
                                                            <th className="p-2"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {g.items.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')).map((p) => (
                                                            <tr key={p.id} className={`border-t ${p.stato === 'scaduta' ? 'bg-[hsl(0_80%_98%)]' : ''}`} data-testid={`proforma-row-${p.id}`}>
                                                                <td className="p-2 font-mono text-xs">{p.numero}</td>
                                                                <td className="p-2">{p.tranche_nome || (p.is_sal_tranche ? 'SAL' : t('pages.fat_pf_one_off') || 'Singola')}</td>
                                                                <td className="p-2">
                                                                    {p.stato === 'scaduta'
                                                                        ? <OverdueBadge dataScadenza={p.data_scadenza} size="sm" />
                                                                        : <Badge variant="outline" className={`${pfStateColors[p.stato] || pfStateColors.bozza} border text-[10px]`}>{pfStateLabel[p.stato] || p.stato}</Badge>
                                                                    }
                                                                </td>
                                                                <td className="p-2 text-xs text-[hsl(var(--muted-foreground))]">{(p.data_emissione || p.created_at || '').slice(0, 10)}</td>
                                                                <td className="p-2 text-right font-display tabular-nums">€{Number(p.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                                                                <td className="p-2 text-right whitespace-nowrap">
                                                                    {p.share_token && (
                                                                        <Button size="sm" variant="ghost" onClick={() => window.open(`/p/proforma/${p.share_token}`, '_blank', 'noopener,noreferrer')} title={t('pages.fat_pf_open_client') || 'Apri link cliente'} data-testid={`proforma-link-${p.id}`}>
                                                                            <FileText className="w-4 h-4" />
                                                                        </Button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {avanz >= 99.9 && !hasFatturaFinale && (
                                                <div className="mt-4 p-3 rounded-lg bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/40 text-sm flex items-center justify-between flex-wrap gap-2" data-testid={`proforme-finalize-${cid}`}>
                                                    <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[hsl(35_90%_35%)]" /><strong>{t('pages.fat_pf_ready_final_title') || 'Lavori al 100%'}</strong> · {t('pages.fat_pf_ready_final_body') || 'Puoi emettere la fattura finale dal SAL (detrarrà automaticamente gli anticipi pagati).'}</div>
                                                    <Button size="sm" onClick={() => navigate(`/app/sal?cantiere=${cant.id}`)} className="gap-1 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-semibold">
                                                        {t('pages.fat_pf_finalize_btn') || 'Emetti fattura finale'} →
                                                    </Button>
                                                </div>
                                            )}
                                        </Card>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </TabsContent>
            </Tabs>

            {/* Mobile FAB */}
            <Button
                onClick={() => setOpenNew(true)}
                className="sm:hidden fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full shadow-lg bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] p-0"
                aria-label={t('pages.fat_new')}
                data-testid="fatture-fab"
            >
                <Plus className="w-6 h-6" />
            </Button>

            {/* New invoice */}
            <Dialog open={openNew} onOpenChange={handleOpenNewChange}>
                <DialogContent className="max-w-4xl sm:max-h-[90vh] max-h-screen h-screen sm:h-auto overflow-y-auto p-4 pb-28 sm:p-6 sm:pb-6 rounded-none sm:rounded-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2">
                            <Receipt className="w-6 h-6 text-[hsl(var(--accent))]" />
                            {t('pages.fat_form_title')}
                        </DialogTitle>
                    </DialogHeader>

                    {/* Draft recovery banner */}
                    {draftHint && (
                        <div
                            className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10"
                            data-testid="fattura-draft-banner"
                        >
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                <FileText className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                                <div className="text-xs sm:text-sm leading-snug">
                                    <div className="font-semibold">{t('pages.draft_banner_title')}</div>
                                    <div className="text-[hsl(var(--muted-foreground))]">
                                        {t('pages.draft_banner_hint', { when: formatSavedAt(draftHint.savedAt, i18n.language) })}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-8 border-[hsl(0_60%_60%)]/50 text-[hsl(0_70%_45%)] hover:bg-[hsl(0_70%_96%)]"
                                    onClick={discardFatDraft}
                                    data-testid="fattura-draft-discard"
                                >
                                    <Trash2 className="w-3 h-3 mr-1" /> {t('pages.draft_discard')}
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="text-xs h-8 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(45_100%_48%)] font-bold"
                                    onClick={resumeFatDraft}
                                    data-testid="fattura-draft-resume"
                                >
                                    <Sparkles className="w-3 h-3 mr-1" /> {t('pages.draft_resume')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Quick action: from quote */}
                    {preventivi.length > 0 && (
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-[hsl(var(--border))] p-3 bg-[hsl(var(--muted))]/30">
                            <div>
                                <div className="text-sm font-semibold flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-[hsl(var(--accent))]" />
                                    {t('pages.fat_from_quote_title')}
                                </div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_from_quote_help')}</div>
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={() => setOpenFromQuote(true)} className="gap-1 h-9" data-testid="fattura-from-quote-btn">
                                <FileText className="w-4 h-4" /> {t('pages.fat_from_quote_btn')}
                            </Button>
                        </div>
                    )}
                    {form.preventivo_riferimento && (
                        <div className="mt-2 text-xs text-[hsl(var(--accent))] flex items-center gap-1">
                            <FileText className="w-3 h-3" /> {t('pages.fat_sheet_quote_ref')}: <strong>{form.preventivo_riferimento}</strong>
                        </div>
                    )}

                    <div className="space-y-6 mt-4">
                        {/* Step 1: Client */}
                        <section className="space-y-3">
                            <h4 className="font-display text-lg flex items-center gap-2 pb-1 border-b border-[hsl(var(--border))]">
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] text-xs font-bold">1</span>
                                {t('pages.fat_form_step_client')}
                            </h4>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div><Label>{t('pages.fat_form_rs')} *</Label><Input value={form.cliente_nome} onChange={(e) => setForm({...form, cliente_nome: e.target.value})} className="mt-1 h-11" placeholder="Rossi Costruzioni SRL" data-testid="fattura-cliente-nome" /></div>
                                <div><Label>{t('pages.fat_form_piva')}</Label><Input value={form.cliente_piva} onChange={(e) => setForm({...form, cliente_piva: e.target.value})} className="mt-1 h-11" placeholder="12345678901" /></div>
                                <div><Label>{t('pages.fat_form_cf')}</Label><Input value={form.cliente_codice_fiscale} onChange={(e) => setForm({...form, cliente_codice_fiscale: e.target.value})} className="mt-1 h-11" /></div>
                                <div><Label>{t('pages.fat_form_sdi')}</Label><Input maxLength={7} value={form.cliente_sdi} onChange={(e) => setForm({...form, cliente_sdi: e.target.value})} className="mt-1 h-11" /></div>
                                <div><Label>{t('pages.fat_form_pec')}</Label><Input type="email" value={form.cliente_pec} onChange={(e) => setForm({...form, cliente_pec: e.target.value})} className="mt-1 h-11" /></div>
                                <div><Label>{t('pages.fat_form_client_email')}</Label><Input type="email" value={form.cliente_email} onChange={(e) => setForm({...form, cliente_email: e.target.value})} placeholder="cliente@example.com" className="mt-1 h-11" data-testid="fattura-cliente-email" /></div>
                                <div className="sm:col-span-2"><Label>{t('pages.fat_form_address')}</Label><Input value={form.cliente_indirizzo} onChange={(e) => setForm({...form, cliente_indirizzo: e.target.value})} className="mt-1 h-11" /></div>
                                <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                                    <Input placeholder={t('pages.fat_form_cap')} value={form.cliente_cap} onChange={(e) => setForm({...form, cliente_cap: e.target.value})} className="h-11" />
                                    <Input placeholder={t('pages.fat_form_city')} value={form.cliente_comune} onChange={(e) => setForm({...form, cliente_comune: e.target.value})} className="h-11" />
                                    <Input placeholder={t('pages.fat_form_province')} maxLength={2} value={form.cliente_provincia} onChange={(e) => setForm({...form, cliente_provincia: e.target.value.toUpperCase()})} className="h-11" />
                                </div>
                            </div>
                        </section>

                        {/* Step 2: Dates */}
                        <section className="space-y-3">
                            <h4 className="font-display text-lg flex items-center gap-2 pb-1 border-b border-[hsl(var(--border))]">
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] text-xs font-bold">2</span>
                                {t('pages.fat_form_step_dates')}
                            </h4>
                            <div className="grid sm:grid-cols-3 gap-3">
                                <div><Label>{t('pages.fat_form_date_emission')}</Label><Input type="date" value={form.data_emissione} onChange={(e) => setForm({...form, data_emissione: e.target.value})} className="mt-1 h-11" /></div>
                                <div>
                                    <Label>{t('pages.prev_pt_label')}</Label>
                                    <Select value={form.payment_terms} onValueChange={(v) => {
                                        const scad = computeScadenza(form.data_emissione, v);
                                        setForm({ ...form, payment_terms: v, data_scadenza: scad });
                                    }}>
                                        <SelectTrigger className="mt-1 h-11" data-testid="fattura-payment-terms-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PAYMENT_TERM_OPTIONS.map((o) => (
                                                <SelectItem key={o.value} value={o.value}>{t(o.labelKey)}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {form.data_scadenza && (
                                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                                            {t('pages.prev_pt_due_date')}: <span className="font-mono tabular-nums">{form.data_scadenza}</span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label>{t('pages.fat_form_cantiere')}</Label>
                                    <Select value={form.cantiere_id} onValueChange={(v) => setForm({...form, cantiere_id: v})}>
                                        <SelectTrigger className="mt-1 h-11"><SelectValue placeholder={t('pages.fat_form_cantiere_none')} /></SelectTrigger>
                                        <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </section>

                        {/* Step 3: Lines */}
                        <section className="space-y-3">
                            <div className="flex items-center justify-between pb-1 border-b border-[hsl(var(--border))]">
                                <h4 className="font-display text-lg flex items-center gap-2">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] text-xs font-bold">3</span>
                                    {t('pages.fat_form_step_lines')}
                                </h4>
                                <Button size="sm" onClick={addLine} variant="outline" className="h-9 gap-1" data-testid="fattura-add-line"><Plus className="w-4 h-4" /> {t('common.add')}</Button>
                            </div>
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_form_section_lines_help')}</p>

                            {/* Desktop: structured grid with sticky headers */}
                            <div className="hidden sm:block rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                                <div className="grid grid-cols-12 gap-2 bg-[hsl(var(--muted))] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                                    <div className="col-span-5">{t('pages.fat_form_line_descr')}</div>
                                    <div className="col-span-1 text-right">{t('pages.fat_form_line_qty')}</div>
                                    <div className="col-span-1">{t('pages.fat_form_line_um')}</div>
                                    <div className="col-span-2 text-right">{t('pages.fat_form_line_price')}</div>
                                    <div className="col-span-2 text-right">{t('pages.fat_form_line_iva')}</div>
                                    <div className="col-span-1"></div>
                                </div>
                                <div className="divide-y divide-[hsl(var(--border))]">
                                    {form.righe.map((r, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-center p-2">
                                            <Input className="col-span-5 h-10" placeholder={`${t('pages.fat_form_line_descr')} #${i+1}`} value={r.descrizione} onChange={(e) => updLine(i, 'descrizione', e.target.value)} data-testid={`fattura-line-descr-${i}`} />
                                            <Input className="col-span-1 h-10 text-right tabular-nums" type="number" step="0.01" value={r.quantita} onChange={(e) => updLine(i, 'quantita', e.target.value)} />
                                            <Input className="col-span-1 h-10" value={r.unita} onChange={(e) => updLine(i, 'unita', e.target.value)} />
                                            <Input className="col-span-2 h-10 text-right tabular-nums" type="number" step="0.01" placeholder="0,00" value={r.prezzo_unitario} onChange={(e) => updLine(i, 'prezzo_unitario', e.target.value)} />
                                            <Select value={String(r.aliquota_iva)} onValueChange={(v) => updLine(i, 'aliquota_iva', v)}>
                                                <SelectTrigger className="col-span-2 h-10"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="0">0%</SelectItem>
                                                    <SelectItem value="4">4%</SelectItem>
                                                    <SelectItem value="10">10%</SelectItem>
                                                    <SelectItem value="22">22%</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Button size="sm" variant="ghost" onClick={() => delLine(i)} className="col-span-1 h-10 text-[hsl(var(--destructive))] justify-center" aria-label={t('common.delete') || 'Delete'}><Trash2 className="w-4 h-4" /></Button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Mobile: stacked cards with explicit labels */}
                            <div className="sm:hidden space-y-3">
                                {form.righe.map((r, i) => (
                                    <div key={i} className="rounded-lg border border-[hsl(var(--border))] p-3 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">#{i+1}</span>
                                            <Button size="sm" variant="ghost" onClick={() => delLine(i)} className="h-8 text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                                        </div>
                                        <div>
                                            <Label className="text-xs">{t('pages.fat_form_line_descr')}</Label>
                                            <Input className="mt-1 h-11" value={r.descrizione} onChange={(e) => updLine(i, 'descrizione', e.target.value)} data-testid={`fattura-line-descr-mobile-${i}`} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <Label className="text-xs">{t('pages.fat_form_line_qty')}</Label>
                                                <Input className="mt-1 h-11 text-right tabular-nums" type="number" inputMode="decimal" step="0.01" value={r.quantita} onChange={(e) => updLine(i, 'quantita', e.target.value)} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">{t('pages.fat_form_line_um')}</Label>
                                                <Input className="mt-1 h-11" value={r.unita} onChange={(e) => updLine(i, 'unita', e.target.value)} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">{t('pages.fat_form_line_price')}</Label>
                                                <Input className="mt-1 h-11 text-right tabular-nums" type="number" inputMode="decimal" step="0.01" value={r.prezzo_unitario} onChange={(e) => updLine(i, 'prezzo_unitario', e.target.value)} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">{t('pages.fat_form_line_iva')}</Label>
                                                <Select value={String(r.aliquota_iva)} onValueChange={(v) => updLine(i, 'aliquota_iva', v)}>
                                                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="0">0%</SelectItem>
                                                        <SelectItem value="4">4%</SelectItem>
                                                        <SelectItem value="10">10%</SelectItem>
                                                        <SelectItem value="22">22%</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div className="text-right text-xs text-[hsl(var(--muted-foreground))]">
                                            {t('pages.fat_form_line_subtotal')}: <span className="font-semibold tabular-nums text-[hsl(var(--foreground))]">€{(r.quantita * r.prezzo_unitario).toFixed(2)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Step 4: Advanced (collapsible) */}
                        <section className="space-y-3">
                            <button type="button" onClick={() => setShowAdvanced((s) => !s)} className="w-full flex items-center justify-between pb-1 border-b border-[hsl(var(--border))]" data-testid="fattura-toggle-advanced">
                                <h4 className="font-display text-lg flex items-center gap-2">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] text-xs font-bold">4</span>
                                    {t('pages.fat_form_step_extras')}
                                </h4>
                                <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                            </button>
                            {!showAdvanced && <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_form_advanced_hint')}</p>}
                            {showAdvanced && (
                                <div className="space-y-3 pt-1">
                                    <div>
                                        <label className="flex items-center gap-2 mb-2"><Checkbox checked={form.ritenuta_acconto} onCheckedChange={(v) => setForm({...form, ritenuta_acconto: !!v})} /> {t('pages.fat_form_ritenuta')}</label>
                                        {form.ritenuta_acconto && (
                                            <Input type="number" value={form.ritenuta_percentuale} onChange={(e) => setForm({...form, ritenuta_percentuale: parseFloat(e.target.value) || 0})} className="w-32 h-11" placeholder="%" />
                                        )}
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <div><Label>{t('pages.fat_form_causale')}</Label><Textarea rows={2} value={form.causale} onChange={(e) => setForm({...form, causale: e.target.value})} className="mt-1" /></div>
                                        <div><Label>{t('common.notes')}</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} className="mt-1" /></div>
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* Totals */}
                        <div className="invoice-totals-box bg-[hsl(var(--muted))] rounded-lg p-4 print-block">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="text-[hsl(var(--muted-foreground))]">{t('pages.fat_form_imponibile')}</div><div className="text-right tabular-nums">€{totali().imp.toFixed(2)}</div>
                                <div className="text-[hsl(var(--muted-foreground))]">{t('pages.prev_form_iva')}</div><div className="text-right tabular-nums">€{totali().iva.toFixed(2)}</div>
                                {form.ritenuta_acconto && (<><div className="text-[hsl(0_72%_52%)]">{t('pages.fat_form_ritenuta_label', { p: form.ritenuta_percentuale })}</div><div className="text-right tabular-nums text-[hsl(0_72%_52%)]">-€{totali().rit.toFixed(2)}</div></>)}
                                <div className="font-display text-xl border-t pt-2 col-span-1">{t('pages.prev_detail_total')}</div>
                                <div className="font-display text-xl border-t pt-2 text-right tabular-nums" data-testid="fattura-total">€{totali().tot.toFixed(2)}</div>
                            </div>
                        </div>

                        <Button onClick={create} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12" data-testid="fattura-submit-button">{t('pages.fat_form_submit')}</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* From quote dialog */}
            <Dialog open={openFromQuote} onOpenChange={setOpenFromQuote}>
                <DialogContent className="max-w-lg" data-testid="fattura-from-quote-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[hsl(var(--accent))]" />
                            {t('pages.fat_from_quote_title')}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('pages.fat_from_quote_help')}</p>
                    <div className="mt-3 max-h-[55vh] overflow-y-auto divide-y divide-[hsl(var(--border))] rounded-lg border border-[hsl(var(--border))]">
                        {preventivi.length === 0 ? (
                            <div className="p-6 text-center text-sm text-[hsl(var(--muted-foreground))]">{t('pages.prev_empty')}</div>
                        ) : preventivi.map((p) => (
                            <button key={p.id} type="button" onClick={() => applyFromQuote(p.id)} className="w-full text-left p-3 hover:bg-[hsl(var(--muted))]/40 flex items-center justify-between gap-3" data-testid={`fattura-pick-quote-${p.id}`}>
                                <div className="min-w-0">
                                    <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{p.numero}</div>
                                    <div className="font-semibold truncate">{p.cliente}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{p.categoria}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display text-lg tabular-nums">€{p.contenuto?.totale_finale?.toLocaleString('it-IT') || '0'}</div>
                                    <div className="text-xs text-[hsl(var(--accent))]">{t('pages.fat_from_quote_apply')} →</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Detail */}
            <Dialog open={!!openDetail} onOpenChange={() => setOpenDetail(null)}>
                <DialogContent className="max-w-3xl sm:max-h-[90vh] max-h-screen h-screen sm:h-auto overflow-y-auto p-0 sm:p-0 rounded-none sm:rounded-lg">
                    {openDetail && (
                        <div data-testid="fattura-detail">
                            {/* Sticky action bar (no-print) */}
                            <div className="no-print sticky top-0 z-10 bg-[hsl(var(--background))]/95 backdrop-blur border-b border-[hsl(var(--border))] px-4 sm:px-6 py-3 flex flex-wrap gap-2 items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {openDetail.stato === 'scaduta'
                                        ? <OverdueBadge dataScadenza={openDetail.data_scadenza} size="md" />
                                        : <Badge className={STATO_COLORS[openDetail.stato]}>{STATUS_LABEL[openDetail.stato] || openDetail.stato}</Badge>
                                    }
                                    <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{openDetail.numero}</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {!editMode ? (
                                        <>
                                            <Button onClick={startEdit} variant="outline" size="sm" className="gap-1 h-9" data-testid="detail-edit-button"><Pencil className="w-4 h-4" /> <span className="hidden sm:inline">{t('pages.fat_action_edit')}</span></Button>
                                            <Button onClick={() => window.print()} variant="outline" size="sm" className="gap-1 h-9"><Printer className="w-4 h-4" /> <span className="hidden sm:inline">{t('common.print')}</span></Button>
                                            <Button onClick={() => downloadPDF(openDetail.id)} size="sm" variant="outline" className="gap-1 h-9 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-semibold hover:bg-[hsl(45_100%_48%)] border-[hsl(var(--accent))]" data-testid="detail-pdf-button"><FileText className="w-4 h-4" /> PDF</Button>
                                            <Button onClick={() => downloadXML(openDetail.id)} size="sm" variant="outline" className="gap-1 h-9" data-testid="detail-xml-button"><FileDown className="w-4 h-4" /> XML</Button>
                                            <Button onClick={sendToSDI} disabled={sdiSending} size="sm" className="gap-1 h-9 bg-[hsl(220_13%_10%)] text-white hover:bg-[hsl(220_13%_18%)]" data-testid="detail-sdi-button">
                                                {sdiSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                                <span className="hidden sm:inline">{t('pages.fat_sdi_send')}</span>
                                                <span className="sm:hidden">{t('pages.fat_sdi_send_short')}</span>
                                            </Button>
                                            <Button onClick={() => setEmailDialog({fattura: openDetail, type: 'send'})} size="sm" className="gap-1 h-9 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold" data-testid="detail-email-button"><Mail className="w-4 h-4" /> <span className="hidden sm:inline">{t('common.email')}</span></Button>
                                            <Button onClick={() => setFollowUp(openDetail)} size="sm" variant="outline" className="gap-1 h-9" data-testid="detail-followup-button"><Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" /> <span className="hidden sm:inline">AI</span></Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button onClick={saveEdit} disabled={savingEdit} size="sm" className="gap-1 h-9 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold" data-testid="detail-save-edit">
                                                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('pages.fat_edit_save')}
                                            </Button>
                                            <Button onClick={cancelEdit} variant="outline" size="sm" className="gap-1 h-9" data-testid="detail-cancel-edit"><X className="w-4 h-4" /> {t('pages.fat_edit_cancel')}</Button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Invoice sheet - looks like a real invoice */}
                            <div className="invoice-sheet bg-white text-[hsl(220_13%_10%)] p-6 sm:p-10">
                                {/* Header bar */}
                                <div className="invoice-header-bar -mx-6 sm:-mx-10 -mt-6 sm:-mt-10 mb-6 px-6 sm:px-10 py-4 bg-[hsl(var(--accent))]">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="invoice-logo-square h-12 w-12 rounded-md bg-[hsl(220_13%_10%)] text-[hsl(var(--accent))] flex items-center justify-center font-display text-lg font-bold">
                                                {companyInitials}
                                            </div>
                                            <div>
                                                <div className="font-display text-xl leading-tight">{user?.company_name || ''}</div>
                                                <div className="text-xs opacity-80">P.IVA {user?.piva || '—'} · SDI {user?.sdi_code || '0000000'}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs uppercase tracking-widest opacity-80">{t('pages.fat_sheet_doc')}</div>
                                            <div className="font-display text-2xl tabular-nums">{openDetail.numero}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Parties */}
                                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{t('pages.fat_sheet_from')}</div>
                                        <div className="font-semibold">{user?.company_name || '—'}</div>
                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">P.IVA {user?.piva || '—'}</div>
                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">SDI {user?.sdi_code || '0000000'}</div>
                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{user?.email}</div>
                                    </div>
                                    <div className="sm:text-right">
                                        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{t('pages.fat_sheet_to')}</div>
                                        <div className="font-semibold">{openDetail.cliente_nome}</div>
                                        {openDetail.cliente_piva && <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.fat_form_piva')} {openDetail.cliente_piva}</div>}
                                        {openDetail.cliente_codice_fiscale && <div className="text-xs text-[hsl(var(--muted-foreground))]">CF {openDetail.cliente_codice_fiscale}</div>}
                                        {openDetail.cliente_indirizzo && <div className="text-xs text-[hsl(var(--muted-foreground))]">{openDetail.cliente_indirizzo}</div>}
                                        {(openDetail.cliente_cap || openDetail.cliente_comune) && <div className="text-xs text-[hsl(var(--muted-foreground))]">{[openDetail.cliente_cap, openDetail.cliente_comune, openDetail.cliente_provincia].filter(Boolean).join(' ')}</div>}
                                        {openDetail.cliente_sdi && <div className="text-xs text-[hsl(var(--muted-foreground))]">SDI {openDetail.cliente_sdi}</div>}
                                    </div>
                                </div>

                                {/* Dates band */}
                                <div className="grid grid-cols-3 gap-3 mb-6 rounded-lg border border-[hsl(var(--border))] divide-x divide-[hsl(var(--border))] overflow-hidden">
                                    <div className="p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{t('pages.fat_sheet_doc_n')}</div>
                                        <div className="font-mono text-sm">{openDetail.numero}</div>
                                    </div>
                                    <div className="p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{t('pages.fat_sheet_issued')}</div>
                                        <div className="font-mono text-sm">{openDetail.data_emissione}</div>
                                    </div>
                                    <div className="p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{t('pages.fat_sheet_due')}</div>
                                        <div className="font-mono text-sm">{openDetail.data_scadenza}</div>
                                    </div>
                                </div>

                                {/* Lines - desktop (READ-ONLY or EDITABLE) */}
                                {!editMode ? (
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="w-full text-sm mb-4">
                                            <thead className="bg-[hsl(var(--muted))]">
                                                <tr>
                                                    <th className="p-2 text-left font-semibold text-xs uppercase tracking-wider">{t('pages.fat_form_line_descr')}</th>
                                                    <th className="p-2 text-right font-semibold text-xs uppercase tracking-wider">{t('pages.fat_form_line_qty')}</th>
                                                    <th className="p-2 text-right font-semibold text-xs uppercase tracking-wider">{t('pages.fat_form_line_price')}</th>
                                                    <th className="p-2 text-right font-semibold text-xs uppercase tracking-wider">{t('pages.fat_form_line_iva')}</th>
                                                    <th className="p-2 text-right font-semibold text-xs uppercase tracking-wider">{t('pages.prev_col_totale')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(openDetail.righe || []).map((r, i) => (
                                                    <tr key={i} className="border-t border-[hsl(var(--border))]">
                                                        <td className="p-2">{r.descrizione}</td>
                                                        <td className="p-2 text-right tabular-nums">{r.quantita} {r.unita}</td>
                                                        <td className="p-2 text-right tabular-nums">€{r.prezzo_unitario.toFixed ? r.prezzo_unitario.toFixed(2) : r.prezzo_unitario}</td>
                                                        <td className="p-2 text-right tabular-nums">{r.aliquota_iva}%</td>
                                                        <td className="p-2 text-right font-semibold tabular-nums">€{(r.quantita * r.prezzo_unitario).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="mb-4 space-y-3 p-3 rounded-lg border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 no-print">
                                        {/* Editable client + dates */}
                                        <div className="grid sm:grid-cols-2 gap-2">
                                            <div><Label className="text-xs">{t('pages.fat_form_rs')}</Label><Input value={editForm.cliente_nome} onChange={(e) => setEditForm({ ...editForm, cliente_nome: e.target.value })} className="mt-1 h-9" /></div>
                                            <div><Label className="text-xs">{t('pages.fat_form_piva')}</Label><Input value={editForm.cliente_piva} onChange={(e) => setEditForm({ ...editForm, cliente_piva: e.target.value })} className="mt-1 h-9" /></div>
                                            <div><Label className="text-xs">{t('pages.fat_form_sdi')}</Label><Input maxLength={7} value={editForm.cliente_sdi} onChange={(e) => setEditForm({ ...editForm, cliente_sdi: e.target.value })} className="mt-1 h-9" /></div>
                                            <div><Label className="text-xs">{t('pages.fat_form_pec')}</Label><Input value={editForm.cliente_pec} onChange={(e) => setEditForm({ ...editForm, cliente_pec: e.target.value })} className="mt-1 h-9" /></div>
                                            <div><Label className="text-xs">{t('pages.fat_form_date_emission')}</Label><Input type="date" value={editForm.data_emissione} onChange={(e) => setEditForm({ ...editForm, data_emissione: e.target.value })} className="mt-1 h-9" /></div>
                                            <div><Label className="text-xs">{t('pages.fat_form_date_due')}</Label><Input type="date" value={editForm.data_scadenza} onChange={(e) => setEditForm({ ...editForm, data_scadenza: e.target.value })} className="mt-1 h-9" /></div>
                                            <div className="sm:col-span-2"><Label className="text-xs">{t('pages.fat_form_causale')}</Label><Input value={editForm.causale} onChange={(e) => setEditForm({ ...editForm, causale: e.target.value })} className="mt-1 h-9" /></div>
                                        </div>
                                        {/* Editable lines */}
                                        <div className="flex items-center justify-between pt-2 border-t border-[hsl(var(--border))]">
                                            <Label className="text-xs font-semibold">{t('pages.fat_form_step_lines')}</Label>
                                            <Button onClick={addEditLine} size="sm" variant="outline" className="gap-1 h-8" data-testid="edit-fattura-add-line"><Plus className="w-3 h-3" /> {t('pages.fat_edit_riga_add')}</Button>
                                        </div>
                                        <div className="space-y-2">
                                            {(editForm.righe || []).map((r, i) => (
                                                <div key={i} className="p-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-2" data-testid={`edit-fattura-line-${i}`}>
                                                    <div>
                                                        <Label className="text-xs">{t('pages.fat_form_line_descr')}</Label>
                                                        <Textarea rows={2} value={r.descrizione} onChange={(e) => updEditLine(i, 'descrizione', e.target.value)} className="mt-1 text-sm" />
                                                    </div>
                                                    <div className="grid grid-cols-12 gap-2 items-end">
                                                        <div className="col-span-4 sm:col-span-2">
                                                            <Label className="text-xs">{t('pages.fat_form_line_qty')}</Label>
                                                            <Input type="number" inputMode="decimal" step="0.01" value={r.quantita} onChange={(e) => updEditLine(i, 'quantita', e.target.value)} className="mt-1 h-9" />
                                                        </div>
                                                        <div className="col-span-4 sm:col-span-2">
                                                            <Label className="text-xs">{t('pages.fat_form_line_um')}</Label>
                                                            <Input value={r.unita} onChange={(e) => updEditLine(i, 'unita', e.target.value)} className="mt-1 h-9" />
                                                        </div>
                                                        <div className="col-span-4 sm:col-span-3">
                                                            <Label className="text-xs">{t('pages.fat_form_line_price')} €</Label>
                                                            <Input type="number" inputMode="decimal" step="0.01" value={r.prezzo_unitario} onChange={(e) => updEditLine(i, 'prezzo_unitario', e.target.value)} className="mt-1 h-9" />
                                                        </div>
                                                        <div className="col-span-6 sm:col-span-2">
                                                            <Label className="text-xs">{t('pages.fat_form_line_iva')}</Label>
                                                            <Select value={String(r.aliquota_iva)} onValueChange={(v) => updEditLine(i, 'aliquota_iva', v)}>
                                                                <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="0">0%</SelectItem>
                                                                    <SelectItem value="4">4%</SelectItem>
                                                                    <SelectItem value="10">10%</SelectItem>
                                                                    <SelectItem value="22">22%</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div className="col-span-4 sm:col-span-2 text-right">
                                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.prev_col_totale')}</div>
                                                            <div className="font-semibold tabular-nums">€{(r.quantita * r.prezzo_unitario).toFixed(2)}</div>
                                                        </div>
                                                        <div className="col-span-2 sm:col-span-1 flex justify-end">
                                                            <Button onClick={() => delEditLine(i)} size="sm" variant="ghost" className="text-[hsl(var(--destructive))] h-9" data-testid={`edit-fattura-del-line-${i}`}><Trash2 className="w-3 h-3" /></Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Lines - mobile (only when NOT in edit mode; edit mode shows full editor above) */}
                                {!editMode && (
                                    <div className="md:hidden space-y-2 mb-4">
                                        {(openDetail.righe || []).map((r, i) => (
                                            <div key={i} className="p-3 rounded-md border border-[hsl(var(--border))]">
                                                <div className="flex items-start justify-between gap-3 mb-1">
                                                    <div className="font-medium text-sm">{r.descrizione}</div>
                                                    <div className="font-semibold tabular-nums whitespace-nowrap">€{(r.quantita * r.prezzo_unitario).toFixed(2)}</div>
                                                </div>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))] tabular-nums">{r.quantita} {r.unita} × €{r.prezzo_unitario} · IVA {r.aliquota_iva}%</div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Totals + payment */}
                                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
                                        <div><span className="font-semibold text-[hsl(var(--foreground))]">{t('pages.fat_sheet_payment_label')}:</span> {openDetail.metodo_pagamento || 'MP05'}</div>
                                        {openDetail.causale && <div><span className="font-semibold text-[hsl(var(--foreground))]">{t('pages.fat_form_causale')}:</span> {openDetail.causale}</div>}
                                        {openDetail.note && <div className="italic">{openDetail.note}</div>}
                                    </div>
                                    <div className="invoice-totals-box rounded-lg bg-[hsl(var(--muted))]/60 p-4 print-block">
                                        <div className="space-y-1 text-sm">
                                            {editMode ? (
                                                <>
                                                    <div className="flex justify-between"><span>{t('pages.fat_form_imponibile')}</span><span className="tabular-nums">€{editTotali().imp.toFixed(2)}</span></div>
                                                    <div className="flex justify-between"><span>{t('pages.prev_form_iva')}</span><span className="tabular-nums">€{editTotali().iva.toFixed(2)}</span></div>
                                                    {editForm?.ritenuta_acconto && <div className="flex justify-between text-[hsl(0_72%_52%)]"><span>{t('pages.fat_form_ritenuta_label', { p: editForm.ritenuta_percentuale })}</span><span className="tabular-nums">-€{editTotali().rit.toFixed(2)}</span></div>}
                                                    <div className="flex justify-between font-display text-2xl border-t border-[hsl(var(--border))] pt-2 mt-2">
                                                        <span>{t('pages.fat_sheet_total_due')}</span>
                                                        <span className="tabular-nums" data-testid="fattura-detail-total">€{editTotali().tot.toFixed(2)}</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="flex justify-between"><span>{t('pages.fat_form_imponibile')}</span><span className="tabular-nums">€{Number(openDetail.imponibile).toFixed(2)}</span></div>
                                                    <div className="flex justify-between"><span>{t('pages.prev_form_iva')}</span><span className="tabular-nums">€{Number(openDetail.iva_totale).toFixed(2)}</span></div>
                                                    {openDetail.ritenuta_acconto && <div className="flex justify-between text-[hsl(0_72%_52%)]"><span>{t('pages.fat_form_ritenuta_label', { p: openDetail.ritenuta_percentuale })}</span><span className="tabular-nums">-€{Number(openDetail.ritenuta_importo).toFixed(2)}</span></div>}
                                                    <div className="flex justify-between font-display text-2xl border-t border-[hsl(var(--border))] pt-2 mt-2">
                                                        <span>{t('pages.fat_sheet_total_due')}</span>
                                                        <span className="tabular-nums" data-testid="fattura-detail-total">€{Number(openDetail.totale).toFixed(2)}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="border-t border-[hsl(var(--border))] pt-4 text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap items-center justify-between gap-2">
                                    <span>{t('pages.fat_sheet_thanks')}</span>
                                    <span className="opacity-70">CantiereAI · {user?.company_name}</span>
                                </div>
                            </div>

                            {/* Quick state actions (no-print) */}
                            <div className="no-print px-4 sm:px-6 py-3 border-t border-[hsl(var(--border))] flex flex-wrap gap-2 justify-end">
                                {openDetail.stato !== 'pagata' && <Button onClick={() => { setStato(openDetail.id, 'pagata'); setOpenDetail(null); }} className="bg-[hsl(145_55%_34%)] hover:bg-[hsl(145_55%_28%)] text-white h-10">{t('pages.fat_action_paid')}</Button>}
                                {openDetail.stato === 'bozza' && <Button onClick={() => { setStato(openDetail.id, 'inviata'); setOpenDetail(null); }} variant="outline" className="h-10">{t('pages.fat_action_invia')}</Button>}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <SendEmailDialog
                open={!!emailDialog}
                onOpenChange={(o) => !o && setEmailDialog(null)}
                endpoint={emailDialog ? (emailDialog.type === 'reminder' ? `/email/fattura/${emailDialog.fattura.id}/reminder` : `/email/fattura/${emailDialog.fattura.id}`) : ''}
                docLabel={emailDialog?.type === 'reminder' ? 'sollecito' : 'fattura'}
                onSent={() => load()}
            />

            <FollowUpDialog
                open={!!followUp}
                onOpenChange={(o) => !o && setFollowUp(null)}
                tipo="fattura"
                riferimentoId={followUp?.id}
                riferimentoLabel={followUp ? `${followUp.numero} · ${followUp.cliente_nome}` : ''}
            />

            {/* SDI not configured dialog */}
            <AlertDialog open={sdiNotConfiguredOpen} onOpenChange={setSdiNotConfiguredOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.fat_sdi_not_configured_title')}</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            <p>{t('pages.fat_sdi_not_configured_msg')}</p>
                            <ul className="list-disc pl-5 text-sm space-y-1">
                                <li><strong>{t('pages.fat_sdi_provider_aruba')}</strong> — €4-15/anno, API REST</li>
                                <li><strong>{t('pages.fat_sdi_provider_fic')}</strong> — Standard di settore</li>
                                <li><strong>{t('pages.fat_sdi_provider_acube')}</strong> — Specializzato Italia</li>
                                <li><strong>{t('pages.fat_sdi_provider_pec')}</strong> — Senza integrazione, manuale</li>
                            </ul>
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                Nel frattempo puoi scaricare l'XML SDI con il pulsante <strong>XML</strong> e inviarlo manualmente.
                            </p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel') || 'Chiudi'}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { setSdiNotConfiguredOpen(false); if (openDetail) downloadXML(openDetail.id); }} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(45_100%_48%)]">
                            <FileDown className="w-4 h-4 mr-1" /> Scarica XML
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
